import React from "react";
import { Link } from "react-router-dom";
import { Moon, Battery, Eye, Leaf, Smartphone, ArrowLeft, ExternalLink, Heart } from "lucide-react";

const ENERGY_IMG = "https://static.prod-images.emergentagent.com/jobs/7a30151d-7fc6-469f-9d14-e6298971f072/images/a32fa2b1431acd36d9f52c29cd90675369d85cdc6fd431d1c0e727c59e8d0bcf.png";
const EYES_IMG = "https://static.prod-images.emergentagent.com/jobs/7a30151d-7fc6-469f-9d14-e6298971f072/images/e6d6b0d76ee3dca4b65aa66357f300c25879774ac0d7fa4642d602c094bdf748.png";

const guides = [
  {
    app: "WhatsApp",
    icon: "https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg",
    steps: [
      "Apri WhatsApp > Impostazioni (3 puntini)",
      "Chat > Tema",
      "Seleziona 'Scuro'",
    ],
    color: "#25D366",
  },
  {
    app: "Google (Chrome)",
    icon: "https://upload.wikimedia.org/wikipedia/commons/8/87/Google_Chrome_icon_%282011%29.svg",
    steps: [
      "Apri Chrome > Impostazioni",
      "Tema > Scuro",
      "Oppure cerca chrome://flags > 'dark mode'",
    ],
    color: "#4285F4",
  },
  {
    app: "LinkedIn",
    icon: "https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png",
    steps: [
      "Profilo > Impostazioni e privacy",
      "Accessibilita' > Modalita' scura",
      "Seleziona 'Scuro'",
    ],
    color: "#0A66C2",
  },
  {
    app: "Instagram",
    icon: "https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png",
    steps: [
      "Profilo > Impostazioni",
      "Tema > Modalita' scura",
      "Su iOS/Android segue il tema di sistema",
    ],
    color: "#E1306C",
  },
  {
    app: "iPhone / iOS",
    icon: "https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg",
    steps: [
      "Impostazioni > Schermo e luminosita'",
      "Seleziona 'Scuro'",
      "Attiva 'Automatico' per cambio giorno/notte",
    ],
    color: "#999",
  },
  {
    app: "Android",
    icon: "https://upload.wikimedia.org/wikipedia/commons/d/d7/Android_robot.svg",
    steps: [
      "Impostazioni > Display",
      "Tema scuro > Attiva",
      "Programmazione automatica disponibile",
    ],
    color: "#3DDC84",
  },
];

const stats = [
  { icon: Battery, value: "47%", label: "Risparmio batteria (OLED)", desc: "I pixel neri sono fisicamente SPENTI" },
  { icon: Eye, value: "-60%", label: "Affaticamento visivo", desc: "Meno luce blu, meno stress oculare" },
  { icon: Leaf, value: "3.5 TWh", label: "Risparmio globale/anno", desc: "Se tutti usassero il dark mode" },
];

export default function DarkModePage() {
  return (
    <div className="min-h-screen bg-[#0c1220] text-white">
      {/* Hero */}
      <section className="py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0c1220] via-[#0f1729] to-[#0c1220]"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Torna alla home
          </Link>
          <div className="flex items-center gap-3 mb-6">
            <Moon className="w-8 h-8 text-[#FBBC04]" />
            <span className="text-sm text-gray-400 uppercase tracking-widest font-bold">Perche' siamo cosi' dannatamente dark?</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight">
            Non siamo <span className="text-[#FBBC04]">impazziti</span>.
            <br />Ci <span className="text-[#34A853]">prendiamo cura</span> di te.
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl leading-relaxed mb-8">
            Ogni pixel nero che vedi su questo sito e' un pixel <strong className="text-white">spento</strong>.
            Meno energia consumata. Meno CO2. Meno stress ai tuoi occhi.
            Non e' solo design — e' una <strong className="text-[#ff6b35]">scelta di responsabilita'</strong>.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 border-y border-white/5">
        <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-3 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <s.icon className="w-8 h-8 text-[#FBBC04] mx-auto mb-3" />
              <div className="text-4xl font-black text-white mb-1">{s.value}</div>
              <div className="text-sm font-bold text-gray-300">{s.label}</div>
              <div className="text-xs text-gray-500 mt-1">{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Two columns — Energy + Eyes */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-12">
          {/* Energy */}
          <div>
            <img src={ENERGY_IMG} alt="Risparmio energetico dark mode vs light mode" className="w-full rounded-2xl mb-6 shadow-2xl shadow-yellow-500/10" />
            <h2 className="text-2xl font-black mb-4">
              <Battery className="w-6 h-6 text-[#FBBC04] inline mr-2" />
              Risparmio <span className="text-[#FBBC04]">Energetico</span>
            </h2>
            <div className="space-y-3 text-gray-300 text-sm leading-relaxed">
              <p><strong className="text-white">OLED/AMOLED:</strong> I pixel neri sono fisicamente spenti. Risparmio fino al <strong className="text-[#FBBC04]">39-47%</strong> di batteria con luminosita' al massimo.</p>
              <p><strong className="text-white">LCD:</strong> Risparmio minore, ma abbassare la luminosita' in combinazione con il dark mode aiuta comunque.</p>
              <p><strong className="text-white">Impatto globale:</strong> Se ogni sito web adottasse il dark mode, risparmieremmo <strong className="text-[#34A853]">3.5 TWh all'anno</strong> — l'equivalente del consumo energetico di una citta' di mezzo milione di abitanti.</p>
            </div>
          </div>
          {/* Eyes */}
          <div>
            <img src={EYES_IMG} alt="Riduzione stress oculare dark mode" className="w-full rounded-2xl mb-6 shadow-2xl shadow-blue-500/10" />
            <h2 className="text-2xl font-black mb-4">
              <Eye className="w-6 h-6 text-[#4285F4] inline mr-2" />
              I tuoi <span className="text-[#4285F4]">Occhi</span>
            </h2>
            <div className="space-y-3 text-gray-300 text-sm leading-relaxed">
              <p><strong className="text-white">Meno luce blu:</strong> La modalita' scura riduce l'emissione di luce blu e il riverbero, specialmente in ambienti poco illuminati.</p>
              <p><strong className="text-white">Meno affaticamento:</strong> Il contrasto ridotto tra schermo e ambiente diminuisce bruciore, secchezza e astenopia.</p>
              <p><strong className="text-white">Regola 20-20-20:</strong> Ogni 20 minuti, guarda un oggetto a 6 metri di distanza per almeno 20 secondi. I tuoi occhi ti ringrazieranno.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-gradient-to-r from-[#0f1729] to-[#0c1220] border-y border-white/5">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Leaf className="w-10 h-10 text-[#34A853] mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-black mb-4">
            Se tutto il mondo facesse come noi...
          </h2>
          <p className="text-gray-400 mb-2">Ogni giorno, ogni sito, ogni app.</p>
          <div className="flex flex-wrap justify-center gap-6 text-sm my-6">
            <div className="bg-white/5 border border-white/10 rounded-xl px-5 py-3">
              <div className="text-2xl font-black text-[#34A853]">-15M</div>
              <div className="text-gray-400 text-xs">tonnellate CO2/anno</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl px-5 py-3">
              <div className="text-2xl font-black text-[#FBBC04]">-47%</div>
              <div className="text-gray-400 text-xs">consumo batteria</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl px-5 py-3">
              <div className="text-2xl font-black text-[#4285F4]">3.5B</div>
              <div className="text-gray-400 text-xs">occhi meno stanchi</div>
            </div>
          </div>
          <p className="text-gray-500 text-sm">Noi ci prendiamo cura della tua scheda business. E non solo.</p>
        </div>
      </section>

      {/* Guides — How to enable dark mode */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-black text-center mb-3">
            Attiva il <span className="text-[#FBBC04]">Dark Mode</span> ovunque
          </h2>
          <p className="text-gray-400 text-center mb-10 text-sm">
            Rendi la tua esperienza digitale appagante. Ecco come fare.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {guides.map((g) => (
              <div key={g.app} className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 hover:border-white/20 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <img src={g.icon} alt={g.app} className="w-8 h-8 object-contain" />
                  <span className="font-bold text-white">{g.app}</span>
                </div>
                <ol className="space-y-2">
                  {g.steps.map((step, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                      <span className="shrink-0 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center mt-0.5" style={{ background: g.color + '22', color: g.color }}>{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 text-center border-t border-white/5">
        <div className="max-w-3xl mx-auto px-4">
          <Heart className="w-8 h-8 text-[#EA4335] mx-auto mb-4" />
          <p className="text-lg text-gray-300 mb-2">
            <strong className="text-white">Ci ricorderai per questo dettaglio.</strong>
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Perche' i dettagli insignificanti fanno la differenza. Sposerai la causa?
          </p>
          <Link to="/" className="inline-flex items-center gap-2 bg-[#ff6b35] hover:bg-[#e55a2b] text-white font-bold px-8 py-3 rounded-full transition-colors">
            Torna alla Home <ArrowLeft className="w-4 h-4 rotate-180" />
          </Link>
        </div>
      </section>
    </div>
  );
}
